import React, { useState } from 'react';

import { css } from '@emotion/react';
import { Box, Text } from '@opendoor/bricks-next';
import { OdProtosSellReceptionData_SellerInput_Channel } from '__generated__/athena';

import { ILpComponentContextualFields } from '../../declarations/contentful';
import NewSellerFlowAddressInput from '../shared/NewSellerFlowAddressInput';

interface StickyAddressWidgetProps {
  entry: ILpComponentContextualFields;
}

type WidgetState = 'visible' | 'minimized' | 'expanded';

const StickyAddressWidget: React.FC<StickyAddressWidgetProps> = ({ entry }) => {
  const { analyticsId, addressInputPlaceholderText, trackingKeyword, channel, title, subtitle } =
    entry.fields ?? {};
  const [state, setState] = useState<WidgetState>('visible');

  const handleClick = () => {
    // Only trigger state changes on mobile
    if (typeof window !== 'undefined' && window.innerWidth <= 767) {
      if (state === 'visible') setState('expanded');
      if (state === 'minimized') setState('visible');
    }
  };

  const handleClose = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setState('visible');
  };

  const handleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    setState('minimized');
  };

  const handleUnminimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    setState('visible');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Don't handle keyboard events from input fields to allow typing spaces and using Enter
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.closest('input')) {
      return;
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <>
      {/* Backdrop for mobile expanded state */}
      {state === 'expanded' && (
        <div
          role="presentation"
          aria-hidden="true"
          css={css`
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 99;
            @media (min-width: 768px) {
              display: none;
            }
          `}
          onClick={handleClose}
        />
      )}

      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        css={css`
          /* Desktop: left rail, sticky to top while scrolling through article body */
          grid-column: 1 / 5 !important;
          grid-row: 1 / span 9999;
          position: sticky;
          top: 100px;
          align-self: start;
          z-index: 10;
          height: fit-content;
          padding-right: 32px;

          /* Mobile: sticky to bottom, click to expand to center */
          @media (max-width: 767px) {
            grid-column: 1 / -1 !important;
            grid-row: auto;
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            padding: 0 16px 16px;
            z-index: 100;
            cursor: pointer;
            transition: all 0.3s ease;

            ${state === 'expanded' &&
            `
              top: 80px;
              bottom: auto;
              left: 50%;
              right: auto;
              transform: translateX(-50%);
              width: calc(100% - 32px);
              max-width: 500px;
              cursor: default;
            `}
          }
        `}
      >
        <Box
          backgroundColor="$backgroundTertiary"
          borderRadius="$16x"
          padding="$12x"
          shadowOffset={{ width: 0, height: 4 }}
          shadowRadius={24}
          $smallerThanMD={{
            borderRadius: '$16x',
            shadowRadius: 16,
          }}
        >
          {/* Close button — shown when expanded on mobile */}
          {state === 'expanded' && (
            <Box
              $smallerThanMD={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: '$4x',
              }}
              $largerThanMD={{
                display: 'none',
              }}
            >
              <button
                onClick={handleClose}
                css={css`
                  background: transparent;
                  border: none;
                  font-size: 24px;
                  cursor: pointer;
                  padding: 0;
                  color: #666;
                  line-height: 1;
                `}
              >
                ×
              </button>
            </Box>
          )}

          {/* Title row with hide/expand toggle — toggle only shown on mobile when not expanded */}
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: ${state === 'minimized' ? '0' : '16px'};

              @media (min-width: 768px) {
                display: block;
                margin-bottom: ${title ? '16px' : '0'};
              }
            `}
          >
            {title && (
              <Text tag="p" typography="$subheaderMedium" color="$contentPrimary">
                {title}
              </Text>
            )}
            {state !== 'expanded' && (
              <button
                aria-label={state === 'minimized' ? 'Expand widget' : 'Hide widget'}
                onClick={state === 'minimized' ? handleUnminimize : handleMinimize}
                css={css`
                  background: transparent;
                  border: none;
                  font-size: 18px;
                  cursor: pointer;
                  padding: 0 0 0 8px;
                  color: #666;
                  line-height: 1;
                  flex-shrink: 0;

                  @media (min-width: 768px) {
                    display: none;
                  }
                `}
              >
                {state === 'minimized' ? '▼' : '▲'}
              </button>
            )}
          </div>

          {/* Address content — hidden on mobile when minimized, always shown on desktop */}
          <div
            css={css`
              @media (max-width: 767px) {
                ${state === 'minimized' ? 'display: none;' : ''}
              }
            `}
          >
            {subtitle && (
              <Text tag="p" typography="$bodyMedium" color="$contentTertiary" marginBottom="$8x">
                {subtitle}
              </Text>
            )}
            <NewSellerFlowAddressInput
              inputLocation="article-sticky-address"
              trackingTaxonomy="article_sticky_address"
              analyticsPrefix={analyticsId}
              placeholder={addressInputPlaceholderText ?? 'Enter your home address'}
              hideLabel
              trackingKeywords={trackingKeyword}
              channel={channel as OdProtosSellReceptionData_SellerInput_Channel | undefined}
              size="small"
            />
          </div>
        </Box>
      </div>
    </>
  );
};

export default StickyAddressWidget;
